import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, FormGroup, Label, Modal, Row } from 'reactstrap';
import { useFlexLayout, useTable } from 'react-table';
import Multiselect from 'multiselect-react-dropdown';
import Loader from '../../../loaders';
import api from '../../../../services/api';
import { ComboBox } from '../../../dropdowns';
import { TooltipButton } from '../../../inputs';
import Grid from '../../../Grid';
import { ErrorNotification } from '../../../alerts';

const emptyItem = { projectViewerId: 0, userProfile: null, userProfileId: null, projectViewerDetails: [] };

const getRowKey = (item) => item?.rowKey ?? `id_${item?.projectViewerId}`;

export default function ProjectViewerList(props) {
    const {
        projectId,
        setSelectedProjectId
    } = props;

    const [ loading, setLoading ] = useState(false);
    const [ project, setProject ] = useState(null);
    const [ viewers, setViewers ] = useState([]);
    const [ recruitmentTypes, setRecruitmentTypes ] = useState([]);
    const [ itemInEdit, setItemInEdit ] = useState(null);
    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ errorTitle, setErrorTitle ] = useState(null);
    const [ errorNotification, setErrorNotification ] = useState([]);

    const loadData = useCallback(() => {
        setProject(null);
        setViewers([]);
        setItemInEdit(null);

        if (!projectId)
            return;

        setLoading(true);

        const apiCalls = [
            api.get(`/project/${projectId}`),
            api.get(`/projectviewer/project?projectId=${projectId}`),
            api.get(`/lookup/recruitmenttype`),
        ];

        Promise.all(apiCalls)
            .then((responses) => {
                setProject(responses[0]?.data);
                setViewers(responses[1]?.data ?? []);
                setRecruitmentTypes(responses[2]?.data ?? []);
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false))
        ;
    }, [projectId]);

    useEffect(() => {
        loadData();
    }, []);

    const handleDelete = useCallback((item) => {
        setViewers(current => current.filter(v => getRowKey(v) !== getRowKey(item)));
        setItemInEdit(current => current && getRowKey(current) === getRowKey(item) ? null : current);
    }, []);

    const handleEdit = useCallback((item) => {
        setItemInEdit({
            ...item,
            userProfile: item.userProfile,
            projectViewerDetails: item.projectViewerDetails ?? [],
        });
    }, []);

    const handleApply = useCallback(() => {
        if (!itemInEdit?.userProfileId || !(itemInEdit?.projectViewerDetails ?? []).length)
            return;

        setViewers(current => {
            const key = getRowKey(itemInEdit);
            const existing = current.filter(v => getRowKey(v) === key).length > 0;
            return existing
                ? current.map(v => getRowKey(v) === key ? { ...itemInEdit } : v)
                : [...current, { ...itemInEdit }];
        });

        setItemInEdit(null);
    }, [itemInEdit]);

    const handleSave = useCallback(() => {
        setLoading(true);

        const data = {
            projectId,
            projectViewers: viewers.map(v => ({
                projectViewerId: v.projectViewerId ?? 0,
                projectId: projectId,
                userProfileId: v.userProfileId,
                projectViewerDetails: (v.projectViewerDetails ?? []),
            }))
        };

        api.post(`/projectviewer/project`, JSON.stringify(data))
            .then(() => {
                setLoading(false);
                setSelectedProjectId(null);
            })
            .catch(error => {
                console.error(error);
                setErrorMessage(`${error.response.data}`);
                setErrorTitle("Error");
                setLoading(false);
            })
        ;
    }, [viewers, projectId, setSelectedProjectId]);

    const columns = useMemo(() => [
        {
            Header: "User",
            id: "userProfile",
            accessor: (row) => row.userProfile?.nameAndEmail,
            width: 260,
        },
        {
            Header: "Recruitment Types",
            id: "projectViewerDetails",
            accessor: (row) => (row.projectViewerDetails ?? []).map(type => type.recruitmentType?.recruitmentTypeName).join(", "),
            width: 260,
        },
        {
            Header: "Actions",
            id: "actions",
            width: 110,
            Cell: ({ row }) => (
                <div className="d-flex justify-content-end">
                    <TooltipButton
                        id={`edit_viewer_${row.index}`}
                        title="Edit"
                        className="btn-icon mr-1"
                        color="default"
                        size="sm"
                        type="button"
                        onClick={() => handleEdit(row.original)}
                    >
                        <i className="fas fa-pencil-alt pt-1"></i>
                    </TooltipButton>
                    <TooltipButton
                        id={`delete_viewer_${row.index}`}
                        title="Delete"
                        className="btn-icon"
                        color="warning"
                        size="sm"
                        type="button"
                        onClick={() => handleDelete(row.original)}
                    >
                        <i className="fas fa-trash-alt pt-1"></i>
                    </TooltipButton>
                </div>
            ),
        },
    ], [handleDelete, handleEdit]);

    const defaultColumn = useMemo(() => ({
        minWidth: 30,
        width: 150,
        maxWidth: 400,
    }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data: viewers,
            defaultColumn,
        },
        useFlexLayout
    );

    const isValid = !!itemInEdit?.userProfileId && (itemInEdit?.projectViewerDetails ?? []).length > 0;
    
    useEffect(() => {
        if (!errorMessage || !errorTitle) {
            setErrorNotification([]);
            return;
        }

        const events = {
            onConfirm: () => {
                setErrorMessage(null);
                setErrorTitle(null);
            },
            message: errorMessage,
            title: errorTitle,
        }

        setErrorNotification([<ErrorNotification {...events} />]);
    }, [errorMessage, errorTitle]);

    return (
        <Modal
            isOpen={true}
            className="modal-lg"
            modalClassName="db-example-modal-lg"
        >
        {
            errorNotification?.length > 0 && errorNotification
        }
        {
            loading && <Loader />
        }
            <div className="modal-header">
                <h5 className="modal-title">
                    Manage Project Viewers - {project?.projectName}
                </h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={() => setSelectedProjectId(null)}
                    type="button"
                >
                    <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                </button>
            </div>
            <div className="modal-body">
                <Container fluid>
                    <Row className="mb-2">
                        <Col xs={12} className="text-right">
                            <Button
                                color="default"
                                size="sm"
                                type="button"
                                onClick={() => setItemInEdit({ ...emptyItem, rowKey: `new_${Date.now()}` })}
                            >
                                <i className="fas fa-plus mr-1"></i> Add Viewer
                            </Button>
                        </Col>
                    </Row>
                    {
                        itemInEdit &&
                        <Row className="border rounded p-2 mb-3 mx-0">
                            <Col lg={6}>
                                <FormGroup>
                                    <Label>
                                        Username
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <ComboBox
                                        endpoint="/lookup/users"
                                        isLookup={false}
                                        minLength={1}
                                        idField="email"
                                        valueField="nameAndEmail"
                                        selectedItem={itemInEdit?.userProfile}
                                        onChange={(selectedItem) => {
                                            setItemInEdit({
                                                ...itemInEdit,
                                                userProfile: selectedItem ?? null,
                                                userProfileId: selectedItem?.userProfileId ?? null,
                                            });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col lg={6}>
                                <FormGroup>
                                    <Label>
                                        Recruitment Types
                                        <span className="text-danger">*</span>
                                    </Label>
                                    <Multiselect
                                        className="slim-search"
                                        options={recruitmentTypes}
                                        displayValue="lookupValue"
                                        placeholder="Select Recruitment Types"
                                        selectedValues={itemInEdit?.projectViewerDetails.map(v => ({ lookupId: v.recruitmentTypeId, lookupValue: v.recruitmentType?.recruitmentTypeName })) ?? []}
                                        onSelect={(selected) => setItemInEdit({ ...itemInEdit, projectViewerDetails: selected?.map(v => ({ projectViewerId: itemInEdit?.projectViewerId ?? 0, recruitmentTypeId: v.lookupId, recruitmentType: { recruitmentTypeId: v.lookupId, recruitmentTypeName: v.lookupValue } })) ?? [] })}
                                        onRemove={(selected) => setItemInEdit({ ...itemInEdit, projectViewerDetails: selected?.map(v => ({ projectViewerId: itemInEdit?.projectViewerId ?? 0, recruitmentTypeId: v.lookupId, recruitmentType: { recruitmentTypeId: v.lookupId, recruitmentTypeName: v.lookupValue } })) ?? [] })}
                                        showCheckbox={true}
                                        showArrow={true}
                                        closeOnSelect={false}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} className="text-right">
                                <Button
                                    color="secondary"
                                    size="sm"
                                    type="button"
                                    onClick={() => setItemInEdit(null)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="info"
                                    size="sm"
                                    type="button"
                                    disabled={!isValid}
                                    onClick={() => handleApply()}
                                >
                                    {viewers.filter(v => getRowKey(v) === getRowKey(itemInEdit)).length > 0 ? "Update" : "Add"}
                                </Button>
                            </Col>
                        </Row>
                    }
                    <Row>
                        <Col xs={12}>
                            <Grid
                                height="200px"
                                rows={rows}
                                tableProps={getTableProps()}
                                headerGroups={headerGroups}
                                tableBodyProps={getTableBodyProps()}
                                prepareRow={prepareRow}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className="modal-footer">
                <Button
                    color="secondary"
                    onClick={() => setSelectedProjectId(null)}
                    type="button"
                >
                    Close
                </Button>
                <Button
                    color="info"
                    onClick={() => handleSave()}
                    type="button"
                    disabled={!viewers?.length}
                >
                    Save Changes
                </Button>
            </div>
        </Modal>
    );
};
